import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay } from '@/types/apiError';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridDataSource,
  GridGetRowsParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { FolderContentsSortField, SortOrder } from '@/api/generatedApi';
import { ApiErrorAlert } from '../Errors/ApiErrorAlert';
import { DATA_GRID_LAZY_PAGE_SIZE } from './definitions';
import { serializeFilterModel, SerializedFilters } from '@/utils/columnFilterSerializer';

const getPageFromStart = (start: number) => Math.floor(Number(start) / DATA_GRID_LAZY_PAGE_SIZE) + 1;

const FIELD_MAPPING: Record<string, FolderContentsSortField> = {
  name: FolderContentsSortField.FolderNameAndAssetPath,
  creator: FolderContentsSortField.CreatedBy,
  createdAt: FolderContentsSortField.CreatedAt,
};

const VALID_SORTABLE_FIELDS = Object.keys(FIELD_MAPPING);
const VALID_SORT_ORDERS = Object.values(SortOrder);

const METADATA_FIELD_PREFIX = 'metadata.';

function isMetadataSortField(field: string | undefined): boolean {
  return field?.startsWith(METADATA_FIELD_PREFIX) ?? false;
}

function isValidSortableField(field: string | undefined): field is keyof typeof FIELD_MAPPING {
  return field !== undefined && VALID_SORTABLE_FIELDS.includes(field);
}

function isValidSortOrder(order: string | undefined): order is SortOrder {
  return order !== undefined && VALID_SORT_ORDERS.includes(order.toUpperCase() as SortOrder);
}

export const DataGridLazy = <T extends GridValidRowModel, R extends Record<string, T[]>>({
  columns,
  fetchRows,
  dataKey,
  error,
  disableFilter = false,
  loading = false,
  ...properties
}: {
  columns: GridColDef[];
  fetchRows?: (parameters: {
    page: number;
    perPage: number;
    filters?: SerializedFilters;
    [key: string]: unknown;
  }) => Promise<R & { pageData: { total: number } }>;
  dataKey: keyof R;
  error?: ApiErrorDisplay;
  disableFilter?: boolean;
  loading?: boolean;
} & DataGridProProps<T>) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (parameters: GridGetRowsParams) => {
        if (!fetchRows || !tenantId) {
          return { rows: [], rowCount: 0 };
        }

        const validSortModel = parameters.sortModel?.find(sort => {
          if (!isValidSortableField(sort.field) && !isMetadataSortField(sort.field)) {
            return false;
          }
          const sortValue = sort.sort ?? undefined;
          return sortValue === undefined || isValidSortOrder(sortValue);
        });

        const requestedStart = Number(parameters.start ?? 0);
        const requestedEnd = Number(parameters.end ?? requestedStart + DATA_GRID_LAZY_PAGE_SIZE);

        const firstRequestedPage = getPageFromStart(requestedStart);
        let lastRequestedPage = loading ? firstRequestedPage : getPageFromStart(Math.max(requestedEnd, requestedStart));

        const prefetchedAhead = !loading && requestedEnd > 0;
        if (prefetchedAhead) {
          const potentialLastPage = Math.ceil((requestedEnd + 1) / DATA_GRID_LAZY_PAGE_SIZE) + 1;
          lastRequestedPage = Math.max(lastRequestedPage, potentialLastPage);
        }

        const isMetadataSort = validSortModel?.field && isMetadataSortField(validSortModel.field);
        const sortBy = validSortModel?.field && !isMetadataSort ? FIELD_MAPPING[validSortModel.field] : undefined;
        const sortByMetadataField =
          validSortModel?.field && isMetadataSort
            ? validSortModel.field.slice(METADATA_FIELD_PREFIX.length)
            : undefined;
        const sortOrder = validSortModel?.sort ? (validSortModel.sort.toUpperCase() as SortOrder) : undefined;

        const filters = serializeFilterModel(parameters.filterModel);

        const firstPageResponse = await fetchRows({
          page: firstRequestedPage,
          perPage: DATA_GRID_LAZY_PAGE_SIZE,
          sortBy,
          sortOrder,
          sortByMetadataField,
          filters,
        });
        const totalRowCount = firstPageResponse.pageData.total;

        const alreadyHaveAllData = requestedStart >= totalRowCount;

        const pagePromises: Array<Promise<R & { pageData: { total: number } }>> = [Promise.resolve(firstPageResponse)];

        if (!alreadyHaveAllData) {
          for (let page = firstRequestedPage + 1; page <= lastRequestedPage; page += 1) {
            const pageStartIndex = (page - 1) * DATA_GRID_LAZY_PAGE_SIZE;
            if (pageStartIndex < totalRowCount) {
              pagePromises.push(
                fetchRows({
                  page,
                  perPage: DATA_GRID_LAZY_PAGE_SIZE,
                  sortBy,
                  sortOrder,
                  sortByMetadataField,
                  filters,
                })
              );
            }
          }
        }

        const pageResponses = await Promise.all(pagePromises);

        const effectiveRequestedEnd = prefetchedAhead
          ? Math.min(lastRequestedPage * DATA_GRID_LAZY_PAGE_SIZE - 1, totalRowCount - 1)
          : requestedEnd;

        const rowsInRange: T[] = [] as T[];
        for (const [responseIndex, pageResponse] of pageResponses.entries()) {
          const page = firstRequestedPage + responseIndex;
          const pageData: T[] = pageResponse[dataKey];
          const pageStartIndex = (page - 1) * DATA_GRID_LAZY_PAGE_SIZE;
          const pageEndIndexExclusive = pageStartIndex + DATA_GRID_LAZY_PAGE_SIZE;

          const sliceStart = Math.max(requestedStart, pageStartIndex);
          const sliceEnd = Math.min(effectiveRequestedEnd, pageEndIndexExclusive);

          if (sliceStart <= sliceEnd) {
            const withinPageStart = sliceStart - pageStartIndex;
            const withinPageEnd = sliceEnd - pageStartIndex + 1;
            rowsInRange.push(...pageData.slice(withinPageStart, withinPageEnd));
          }
        }

        return { rows: rowsInRange, rowCount: totalRowCount };
      },
    }),
    [fetchRows, dataKey, loading, tenantId]
  );

  if (error) {
    return <ApiErrorAlert error={error} />;
  }

  return (
    <DataGridPro<T>
      columns={columns}
      lazyLoading
      hideFooter
      loading={loading}
      dataSource={dataSource}
      dataSourceCache={undefined}
      onDataSourceError={noop}
      disableColumnFilter={disableFilter}
      filterDebounceMs={200}
      slotProps={{
        basePopper: {
          placement: 'bottom-start',
        },
      }}
      {...properties}
    />
  );
};
